import { z } from 'zod';
import {
  ApplicationPortfolioView,
  QueryMetadata,
  type Disposition,
  type GlobalFilters,
  type QueryResult,
} from '@aurora-pvi/contracts';
import type { ApplicationRefinement } from '@aurora-pvi/testing';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';
import type { WriteOutcome } from './httpIdentityRepository';

/**
 * The `applications` group of `Repositories`, backed by `GET /applications`.
 *
 * Unlike the other HTTP repositories this one forwards paging to the server:
 * the estate is ~1,900 rows and the route counts, facets and pages over the
 * whole set, so the page argument is a real query parameter rather than a
 * client-side slice.
 *
 * Filters are forwarded too. They used to be refused — a narrowed view fell
 * back to the browser's fixture dataset, so choosing a filter quietly swapped
 * the source of every figure on the screen. The route applies them now.
 *
 * `setDisposition` sits beside `list` rather than on the `Repositories` port,
 * for the same reason the delegation writes do: every method on that port
 * returns a read model, and a command is not a query. Its result is a
 * `WriteOutcome` carrying the server's own refusal text, because the refusals
 * this endpoint issues — a missing permission, an unrecognised disposition, a
 * rationale that says nothing — are the only place a caller learns what the
 * platform requires of a decision.
 */

const Envelope = z.object({ data: ApplicationPortfolioView, meta: QueryMetadata });

export function createHttpApplicationRepository(baseUrl: string) {
  return {
    async list(
      filters: GlobalFilters,
      page: { page: number; pageSize: number },
      selectedId?: string,
      refinement?: ApplicationRefinement,
    ): Promise<QueryResult<ApplicationPortfolioView>> {
      const params = filterParams(filters);
      params.set('page', String(page.page));
      params.set('pageSize', String(page.pageSize));
      for (const key of ['domain', 'businessCapability', 'criticality', 'hostingEnvironment'] as const) {
        for (const value of refinement?.[key] ?? []) params.append(key, value);
      }
      if (refinement?.duplicateStatus && refinement.duplicateStatus !== 'all') {
        params.set('duplicateStatus', refinement.duplicateStatus);
      }
      if (selectedId) params.set('selectedId', selectedId);
      const response = await apiFetch(`${baseUrl}/applications?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`GET /applications failed: ${response.status} ${response.statusText}`);
      }
      const json: unknown = await response.json();
      return Envelope.parse(json);
    },

    /**
     * Records a rationalisation decision against one application.
     *
     * One application at a time even when the caller is deciding on two
     * hundred: each decision is separately audited and separately refusable,
     * and a batch endpoint that half-succeeded would leave the reader unable
     * to say which half. The caller loops and reports per row.
     */
    async setDisposition(
      applicationId: string,
      decision: { disposition: Disposition; rationale: string; confidence: number },
    ): Promise<WriteOutcome> {
      const response = await apiFetch(`${baseUrl}/applications/${applicationId}/disposition`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(decision),
      });
      if (response.ok) return { ok: true };
      const body = (await response.json().catch(() => ({}))) as {
        message?: string;
        problems?: string[];
      };
      // `problems` is the endpoint's list of what a decision must carry. Losing
      // it would reduce "state what, why and how sure" to "request failed".
      const detail = body.problems?.length ? ` ${body.problems.join(' ')}` : '';
      return {
        ok: false,
        message: `${body.message ?? `Request failed (${response.status}).`}${detail}`,
      };
    },
  };
}
